<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center justify-end">
        <f-btn edit small class="ml-2" 
          @click="editMode = !editMode"
        ></f-btn>
      </v-card-title>
      <v-card-text>
        <v-row v-if="!editMode">
          <v-col cols="12" md="6">
            <v-text-field
              :value="currentLabel"
              label="Preferência de Lembrete de Pagamento"
              readonly
              prepend-icon="mdi-bell-outline"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              :value="formatPhoneNumber(currentWhatsappNumber) || 'Não informado'"
              label="Número WhatsApp"
              readonly
              prepend-icon="mdi-whatsapp"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedPreference"
              :items="preferenceOptions"
              label="Preferência de Lembrete de Pagamento"
              item-text="label"
              item-value="value"
              prepend-icon="mdi-bell-outline"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="showPhoneSuggestions"
              :close-on-content-click="false"
              offset-y
              max-width="400"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="selectedWhatsappNumber"
                  v-bind="attrs"
                  v-on="on"
                  label="Número WhatsApp"
                  prepend-icon="mdi-whatsapp"
                  placeholder="(00) 00000-0000"
                  hint="Formato: (DDD) XXXXX-XXXX - Digite ou selecione uma sugestão"
                  persistent-hint
                  clearable
                  v-mask="'(##) #####-####'"
                  autocomplete="off"
                  @input="filterPhoneSuggestions"
                  @focus="showPhoneSuggestions = true"
                ></v-text-field>
              </template>
              <v-list v-if="filteredPhoneSuggestions.length > 0">
                <v-list-item
                  v-for="(item, index) in filteredPhoneSuggestions"
                  :key="index"
                  @click="selectPhoneSuggestion(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.contactName">{{ item.contactName }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list v-else>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text--secondary">Digite um número ou selecione uma sugestão</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          <v-col cols="12" class="d-flex align-center justify-end">
            <f-btn 
              save 
              small 
              @click="savePreference"
              :loading="loading.saving"
              class="ml-2"
            ></f-btn>
            <f-btn 
              cancel 
              small 
              @click="cancelEdit"
              class="ml-2"
            ></f-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Api from '@/api/index'
import { mask } from 'vue-the-mask'

export default {
  name: 'ClientPaymentReminderPreference',
  directives: { mask },
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      editMode: false,
      selectedPreference: null,
      selectedWhatsappNumber: null,
      showPhoneSuggestions: false,
      filteredPhoneSuggestions: [],
      preferenceOptions: [
        { label: 'Não enviar', value: 0, enumValue: 'dont_send' },
        { label: 'Enviar lembretes', value: 10, enumValue: 'send_notifications' }
      ],
      loading: {
        saving: false
      }
    }
  },
  computed: {
    currentPreference() {
      const value = this.client?.data?.attributes?.payment_reminder_preference
      const option = this.preferenceOptions.find(opt => opt.enumValue === value)
      return option ? option.value : 0
    },
    currentLabel() {
      const option = this.preferenceOptions.find(opt => opt.value === this.currentPreference)
      return option ? option.label : 'Não enviar'
    },
    currentWhatsappNumber() {
      return this.client?.data?.attributes?.whatsapp_number || null
    },
    phoneSuggestions() {
      const suggestions = []
      const contacts = this.client?.data?.attributes?.contacts?.data || []
      
      // Adiciona o número atual do whatsapp_number se existir
      if (this.currentWhatsappNumber) {
        const formatted = this.formatPhoneNumber(this.currentWhatsappNumber)
        suggestions.push({
          text: `${formatted || this.currentWhatsappNumber} (Número atual)`,
          value: this.currentWhatsappNumber.replace(/\D/g, ''),
          contactName: 'Número atual'
        })
      }
      
      // Adiciona números de celular dos contatos
      contacts.forEach(contact => {
        const celphone = contact?.attributes?.celphone
        const telephone = contact?.attributes?.telephone
        const contactName = contact?.attributes?.name || 'Sem nome'
        if (celphone && celphone.trim() !== '') {
          // Remove formatação para sugerir apenas números
          const cleanNumber = celphone.replace(/\D/g, '')
          if (cleanNumber && !suggestions.find(s => s.value === cleanNumber)) {
            const formatted = this.formatPhoneNumber(cleanNumber)
            suggestions.push({
              text: `${formatted || cleanNumber} (${contactName})`,
              value: cleanNumber,
              contactName: contactName
            })
          }
        }
        // Também adiciona telefone fixo como opção
        if (telephone && telephone.trim() !== '') {
          const cleanNumber = telephone.replace(/\D/g, '')
          if (cleanNumber && !suggestions.find(s => s.value === cleanNumber)) {
            const formatted = this.formatPhoneNumber(cleanNumber)
            suggestions.push({
              text: `${formatted || cleanNumber} (${contactName} - Fixo)`,
              value: cleanNumber,
              contactName: `${contactName} - Fixo`
            })
          }
        }
      })
      
      return suggestions
    },
  },
  watch: {
    editMode(newVal) {
      if (newVal) {
        this.selectedPreference = this.currentPreference
        this.selectedWhatsappNumber = this.currentWhatsappNumber ? this.formatPhoneNumber(this.currentWhatsappNumber) : null
        this.filteredPhoneSuggestions = this.phoneSuggestions
      }
    },
    selectedWhatsappNumber(newVal) {
      if (newVal) {
        this.filterPhoneSuggestions()
      }
    }
  },
  methods: {
    savePreference() {
      this.loading.saving = true
      // Converte o valor numérico selecionado para a string do enum
      const selectedOption = this.preferenceOptions.find(opt => opt.value === this.selectedPreference)
      const enumValue = selectedOption ? selectedOption.enumValue : 'dont_send'
      
      // Remove formatação do número antes de salvar
      const cleanWhatsappNumber = this.selectedWhatsappNumber 
        ? this.selectedWhatsappNumber.replace(/\D/g, '') 
        : null
      
      const clientData = {
        id: this.client.data.id,
        payment_reminder_preference: enumValue,
        whatsapp_number: cleanWhatsappNumber || null
      }
      
      Api.Client.basicUpdate(clientData)
        .then(() => {
          this.addNotification({ type: 'success', msg: 'Preferência de lembrete atualizada com sucesso' })
          this.$emit('updated')
          this.editMode = false
        })
        .catch(error => {
          console.error(error)
          this.addNotification({ type: 'error', msg: 'Não foi possível atualizar a preferência de lembrete' })
        })
        .finally(() => {
          this.loading.saving = false
        })
    },
    cancelEdit() {
      this.editMode = false
      this.selectedPreference = this.currentPreference
      this.selectedWhatsappNumber = this.currentWhatsappNumber
    },
    filterPhones(item, queryText) {
      const text = item.text.toLowerCase()
      const query = queryText.toLowerCase()
      return text.includes(query) || item.contactName?.toLowerCase().includes(query)
    },
    filterPhoneSuggestions() {
      if (!this.selectedWhatsappNumber) {
        this.filteredPhoneSuggestions = this.phoneSuggestions
        return
      }
      
      const query = this.selectedWhatsappNumber.toLowerCase().replace(/\D/g, '')
      if (!query) {
        this.filteredPhoneSuggestions = this.phoneSuggestions
        return
      }
      
      this.filteredPhoneSuggestions = this.phoneSuggestions.filter(item => {
        const itemValue = item.value.toString().toLowerCase()
        const itemText = item.text.toLowerCase()
        const itemName = item.contactName?.toLowerCase() || ''
        return itemValue.includes(query) || itemText.includes(query) || itemName.includes(query)
      })
    },
    selectPhoneSuggestion(item) {
      this.selectedWhatsappNumber = this.formatPhoneNumber(item.value)
      this.showPhoneSuggestions = false
    },
    formatPhoneNumber(number) {
      if (!number) return null
      // Remove todos os caracteres não numéricos
      const cleanNumber = number.replace(/\D/g, '')
      // Formata como (DDD) XXXXX-XXXX (assumindo que os últimos 11 dígitos são DDD + número)
      if (cleanNumber.length >= 11) {
        const ddd = cleanNumber.slice(-11, -9)
        const firstPart = cleanNumber.slice(-9, -4)
        const secondPart = cleanNumber.slice(-4)
        return `(${ddd}) ${firstPart}-${secondPart}`
      } else if (cleanNumber.length >= 9) {
        // Se tiver menos de 11 dígitos, tenta formatar com os últimos 9
        const ddd = cleanNumber.slice(-9, -7)
        const firstPart = cleanNumber.slice(-7, -4)
        const secondPart = cleanNumber.slice(-4)
        return `(${ddd}) ${firstPart}-${secondPart}`
      }
      return number
    }
  }
}
</script>
